import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  MapPin, Building2, Briefcase, ArrowLeft, Share2,
  FileText, Mail, MessageCircle, Download, Eye, ExternalLink, 
  ShieldCheck, Sparkles, Clock, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { jobsAPI, BACKEND_URL } from '../lib/api';
import { formatSalary } from '../lib/utils';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet';

/* ========== HELPERS DE MÍDIA ========== */
const MEDIA = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path; 
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  return `${BACKEND_URL}/${cleanPath}`;
};

const fallbackImage = "https://empregamaisro.com.br/logo-social.jpg";

const getEmbedUrl = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?\s*v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2]?.length === 11 
    ? `https://www.youtube.com/embed/${match[2]}?rel=0&modestbranding=1`
    : null;
};

const JobDetailPage = () => {
  const { idOrSlug } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadJob = async () => {
      try {
        setLoading(true);
        const res = await jobsAPI.getById(idOrSlug); 
        if (res.data) {
          setJob(res.data);
        }
      } catch (err) {
        console.error("Erro detalhado:", err);
        toast.error("Vaga não encontrada.");
        // Redirecionamento comentado para evitar loops durante testes
        // navigate('/vagas'); 
      } finally {
        setLoading(false);
      }
    };
    loadJob();
  }, [idOrSlug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#020617]">
        <div className="flex flex-col items-center gap-6">
          <div className="relative flex items-center justify-center">
              <div className="w-24 h-24 border-4 border-teal-500/20 rounded-full animate-ping absolute" />
              <div className="animate-spin w-16 h-16 border-[3px] border-teal-500 border-t-transparent rounded-full relative z-10" />
          </div>
          <p className="text-teal-600 dark:text-teal-400 font-black tracking-[0.3em] text-xs animate-pulse">CARREGANDO DETALHES</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-[#020617]">
        <h1 className="text-2xl font-black text-slate-800 dark:text-white mb-4">Vaga não encontrada</h1>
        <Button asChild variant="outline">
          <Link to="/vagas">Voltar para a lista</Link>
        </Button>
      </div>
    );
  }

  // Helpers de conteúdo
  const pdfUrl = job.document_url ? MEDIA(job.document_url) : null;
  const videoEmbed = job.video_url ? getEmbedUrl(job.video_url) : null;
  const finalImage = job.image_url ? MEDIA(job.image_url) : fallbackImage;
  const plainDescription = job.description?.replace(/<[^>]*>/g, '').substring(0, 160) || "Confira esta oportunidade no Emprega Mais Rondônia";

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#020617]">
<Helmet>
  <title>{`${job.title} | Emprega Mais RO`}</title>
  <meta name="description" content={plainDescription} />
  
  {/* ADICIONE ESTA LINHA AQUI: */}
  <link rel="canonical" href={window.location.href} />

  {/* Open Graph / Facebook */}
  <meta property="og:type" content="website" />
  {/* AJUSTE ESTA LINHA TAMBÉM: */}
  <meta property="og:url" content={window.location.href} />
  
  <meta property="og:title" content={`${job.title} | Emprega Mais RO`} />
  <meta property="og:description" content={plainDescription} />
  <meta property="og:image" content={finalImage} />

  {/* Twitter */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={job.title} />
  <meta name="twitter:description" content={plainDescription} />
  <meta name="twitter:image" content={finalImage} />
</Helmet>

      {/* HEADER */}
      <div className="relative overflow-hidden bg-white dark:bg-[#0f172a]/40 border-b border-slate-200 dark:border-slate-800/60 pt-16 pb-20">
        <div className="container mx-auto max-w-6xl px-6 relative z-10">
          <Link to="/vagas" className="group inline-flex items-center gap-3 text-slate-500 dark:text-slate-400 font-bold mb-10 hover:text-teal-600">
            <div className="p-2.5 rounded-2xl bg-slate-100 dark:bg-slate-800 group-hover:bg-teal-500 group-hover:text-white transition-all">
                <ArrowLeft className="w-5 h-5" /> 
            </div>
            <span className="text-sm tracking-tight uppercase">Voltar ao Mural</span>
          </Link>

          <div className="flex flex-col lg:flex-row justify-between items-end gap-10">
            <div className="flex-1 space-y-8">
              <Badge className="bg-teal-600 text-white px-6 py-2 text-[10px] font-black uppercase tracking-widest">
                {job.category || "Vaga Aberta"}
              </Badge>
              
              <div className="flex flex-col md:flex-row gap-8 items-start md:items-center">
                {job.image_url && (
                  <div className="relative w-32 h-32 rounded-[2rem] overflow-hidden border-4 border-white shadow-2xl bg-white shrink-0">
                    <img src={MEDIA(job.image_url)} alt={job.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <h1 className="text-4xl md:text-6xl font-black text-slate-900 dark:text-white tracking-tighter">
                  {job.title}
                </h1>
              </div>

              <div className="flex flex-wrap gap-4">
                 <div className="flex items-center gap-3 bg-white dark:bg-slate-800 p-4 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
                    <Building2 className="w-5 h-5 text-blue-500" />
                    <span className="font-bold dark:text-slate-200">{job.company_name}</span>
                 </div>
                 <div className="flex items-center gap-3 bg-white dark:bg-slate-800 p-4 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
                    <MapPin className="w-5 h-5 text-rose-500" />
                    <span className="font-bold dark:text-slate-200">{job.city}</span>
                 </div>
              </div>
            </div>

            <Button 
              onClick={() => { 
                navigator.clipboard.writeText(window.location.href); 
                toast.success("Link copiado!");
              }}
              className="rounded-[2rem] bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-10 py-8 h-auto text-lg font-black"
            >
              <Share2 className="w-6 h-6 mr-3" /> Compartilhar Link
            </Button>
          </div>
        </div>
      </div>

      {/* CONTEÚDO PRINCIPAL */}
      <div className="container mx-auto px-6 max-w-6xl mt-16 grid grid-cols-1 lg:grid-cols-12 gap-16 pb-20">
        <div className="lg:col-span-8 space-y-12">
          {videoEmbed && (
            <div className="rounded-[3rem] overflow-hidden aspect-video shadow-2xl border-4 border-white dark:border-slate-800">
              <iframe className="w-full h-full" src={videoEmbed} allowFullScreen title="Video Job" />
            </div>
          )}

          <div className="bg-white dark:bg-[#0f172a] rounded-[3rem] p-10 md:p-14 border border-slate-100 dark:border-slate-800 shadow-xl">
             <h2 className="text-3xl font-black mb-8 italic flex items-center gap-3 dark:text-white">
               <div className="h-8 w-2 bg-teal-500 rounded-full" /> Detalhes da Oportunidade
             </h2>
             <div 
                className="prose prose-slate dark:prose-invert max-w-none text-xl leading-relaxed"
                dangerouslySetInnerHTML={{ __html: job.description }}
             />
          </div>

          {pdfUrl && (
            <div className="bg-white dark:bg-[#0f172a] rounded-[3rem] border border-slate-100 dark:border-slate-800 overflow-hidden shadow-2xl">
              <div className="p-8 bg-slate-50 dark:bg-slate-800/50 flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <FileText className="w-8 h-8 text-teal-500" />
                  <span className="font-black dark:text-white">Edital / Documento Auxiliar</span>
                </div>
                <Button className="bg-teal-600 hover:bg-teal-500 text-white rounded-xl" asChild>
                  <a href={pdfUrl} target="_blank" rel="noreferrer">Visualizar PDF</a>
                </Button>
              </div>
              <iframe src={pdfUrl} className="w-full h-[600px] border-none" />
            </div>
          )}
        </div>

        {/* SIDEBAR LATERAL */}
        <div className="lg:col-span-4 space-y-8">
          <div className="sticky top-10 space-y-6">
            <div className="bg-white dark:bg-[#0f172a] rounded-[3.5rem] p-10 shadow-2xl border border-slate-100 dark:border-slate-800">
              <p className="text-[10px] font-black text-teal-500 uppercase tracking-widest mb-2">Remuneração Estimada</p>
              <div className="text-4xl font-black text-slate-900 dark:text-white mb-8">
                {formatSalary(job.salary_min, job.salary_max)}
              </div>

              {job.link ? (
                <Button className="w-full h-20 bg-teal-600 hover:bg-teal-500 text-white font-black text-xl rounded-[2rem] shadow-xl" asChild>
                  <a href={job.link} target="_blank" rel="noreferrer">
                    CANDIDATAR-SE <ExternalLink className="w-6 h-6 ml-3" />
                  </a>
                </Button>
              ) : (
                <div className="space-y-4">
                  {job.contact_email && (
                    <a href={`mailto:${job.contact_email}`} className="flex items-center gap-4 p-5 rounded-2xl bg-slate-50 dark:bg-slate-800 hover:bg-teal-50 transition-colors">
                      <Mail className="w-6 h-6 text-teal-500 shrink-0" />
                      <span className="font-bold truncate dark:text-white">{job.contact_email}</span>
                    </a>
                  )}
                  {job.contact_phone && (
                    <a href={`https://wa.me/55${job.contact_phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 p-5 rounded-2xl bg-slate-50 dark:bg-slate-800 hover:bg-emerald-50 transition-colors">
                      <MessageCircle className="w-6 h-6 text-emerald-500 shrink-0" />
                      <span className="font-bold dark:text-white">{job.contact_phone}</span>
                    </a>
                  )}
                </div>
              )}
            </div>

            <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden group">
               <ShieldCheck className="absolute -bottom-4 -right-4 w-24 h-24 opacity-10" />
               <div className="relative z-10 flex gap-4 items-center">
                 <div className="p-3 bg-white/10 rounded-2xl">
                   <ShieldCheck className="w-8 h-8 text-teal-400" />
                 </div>
                 <div>
                   <h3 className="font-black uppercase text-sm">Vaga Auditada</h3>
                   <p className="text-[10px] text-slate-400">Verificada pela equipa Emprega Mais RO.</p>
                 </div>
               </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailPage;